import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  BarChart3, Brain, Zap, Clock, Coins, TrendingUp, Shield, ArrowRight,
  RefreshCw, Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { friendlyAiProviderName as friendlyProviderName } from '@/lib/providerLabels';

export default function AIDashboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);

  const load = async (p) => {
    try {
      const { data: d } = await api.get(`/tenant/ai/dashboard?period=${p || period}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period]);

  const changePeriod = (p) => { setPeriod(p); setLoading(true); load(p); };

  if (loading) return <PantallaCargando />;
  if (!data) return <p className="text-sm text-[#94A3B8] text-center py-10">Sin datos disponibles</p>;

  const { kpis, by_provider, by_model, time_series, limits, recent_logs } = data;

  return (
    <div className="space-y-6" data-testid="ai-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> Panel IA
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Métricas de uso de inteligencia artificial</p>
        </div>
        <div className="flex gap-2">
          {['daily', 'monthly', 'yearly'].map(p => (
            <button key={p} onClick={() => changePeriod(p)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${period === p ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#94A3B8]'}`}
              data-testid={`period-${p}`}>
              {p === 'daily' ? 'Hoy' : p === 'monthly' ? 'Mes' : 'Año'}
            </button>
          ))}
          <Link to="/tenant/ai/console">
            <Button variant="outline" size="sm" className="text-xs gap-1">
              <Brain className="w-3.5 h-3.5" /> Consola <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="ai-kpi-cards">
        <KPICard icon={Activity} label="Requests" value={kpis.requests} />
        <KPICard icon={Zap} label="Tokens" value={kpis.tokens?.toLocaleString()} />
        <KPICard icon={Coins} label="Costo (MXN)" value={`$${kpis.cost?.toFixed(4)}`} />
        <KPICard icon={TrendingUp} label="Éxito" value={`${kpis.success_rate}%`} color={kpis.success_rate >= 90 ? 'emerald' : 'amber'} />
        <KPICard icon={Clock} label="Latencia avg" value={`${kpis.avg_latency_ms}ms`} />
        <KPICard icon={Shield} label="Fallback" value={`${kpis.fallback_rate}%`} />
      </div>

      {/* Limits bar */}
      {limits && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="ai-limits-section">
          <h3 className="text-xs font-semibold text-[#64748B] mb-2 uppercase tracking-wider">Límites del plan</h3>
          <div className="flex flex-wrap gap-4 text-xs">
            {limits.max_ai_requests_monthly && <LimitPill label="Requests/mes" current={kpis.requests} max={limits.max_ai_requests_monthly} />}
            {limits.max_ai_rpm && <LimitPill label="RPM actual" current={limits.current_rpm || 0} max={limits.max_ai_rpm} />}
            {limits.max_ai_tokens_monthly && <LimitPill label="Tokens/mes" current={kpis.tokens} max={limits.max_ai_tokens_monthly} />}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Provider */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="ai-by-provider">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Por Proveedor</h3>
          {by_provider.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin datos</p> : (
            <div className="space-y-2">
              {by_provider.map(p => (
                <div key={p.provider_key} className="flex items-center justify-between py-1.5 border-b border-[#F1F5F9] last:border-0">
                  <span className="text-xs font-medium text-[#0F172A]">{friendlyProviderName(p.provider_key)}</span>
                  <div className="flex gap-4 text-[11px] text-[#64748B]">
                    <span>{p.requests} req</span>
                    <span>{p.tokens?.toLocaleString()} tok</span>
                    <span>${p.cost?.toFixed(4)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* By Model */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="ai-by-model">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Por Modelo</h3>
          {by_model.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin datos</p> : (
            <div className="space-y-2">
              {by_model.map(m => (
                <div key={m.model_key} className="flex items-center justify-between py-1.5 border-b border-[#F1F5F9] last:border-0">
                  <span className="text-xs font-medium text-[#0F172A]">{m.model_key?.split(':')[1] || m.model_key}</span>
                  <div className="flex gap-4 text-[11px] text-[#64748B]">
                    <span>{m.requests} req</span>
                    <span>{m.tokens?.toLocaleString()} tok</span>
                    <span>${m.cost?.toFixed(4)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Time Series */}
      {time_series.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="ai-time-series">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Serie Temporal</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-[#64748B] border-b border-[#E2E8F0]">
                <th className="text-left py-2 pr-4">Fecha</th>
                <th className="text-right py-2 px-3">Requests</th>
                <th className="text-right py-2 px-3">Tokens</th>
                <th className="text-right py-2 px-3">Costo</th>
              </tr></thead>
              <tbody>
                {time_series.map(ts => (
                  <tr key={ts.date} className="border-b border-[#F1F5F9]">
                    <td className="py-1.5 pr-4 text-[#0F172A]">{ts.date}</td>
                    <td className="text-right py-1.5 px-3">{ts.requests}</td>
                    <td className="text-right py-1.5 px-3">{ts.tokens?.toLocaleString()}</td>
                    <td className="text-right py-1.5 px-3">${ts.cost?.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Logs */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="ai-recent-logs">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Últimos Requests</h3>
        {recent_logs.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin requests aún</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-[#64748B] border-b border-[#E2E8F0]">
                <th className="text-left py-2">Modelo</th>
                <th className="text-left py-2">Estado</th>
                <th className="text-right py-2">Tokens</th>
                <th className="text-right py-2">Latencia</th>
                <th className="text-right py-2">Costo</th>
                <th className="text-right py-2">Fecha</th>
              </tr></thead>
              <tbody>
                {recent_logs.map(l => (
                  <tr key={l.id} className="border-b border-[#F1F5F9]">
                    <td className="py-1.5 text-[#0F172A]">{l.model_key?.split(':')[1] || l.model_key}</td>
                    <td className="py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${l.status === 'success' ? 'bg-emerald-50 text-emerald-600' : l.status === 'blocked' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="text-right py-1.5">{l.total_tokens}</td>
                    <td className="text-right py-1.5">{l.latency_ms}ms</td>
                    <td className="text-right py-1.5">${l.estimated_cost?.toFixed(4)}</td>
                    <td className="text-right py-1.5 text-[#94A3B8]">{l.created_at ? new Date(l.created_at).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  const colorClass = color === 'emerald' ? 'text-emerald-600' : color === 'amber' ? 'text-amber-600' : 'text-[#0F172A]';
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#94A3B8]" />
        <span className="text-[10px] font-medium text-[#64748B] uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-lg font-bold ${colorClass}`}>{value}</p>
    </div>
  );
}

function LimitPill({ label, current, max }) {
  const pct = max > 0 ? Math.min(100, (current / max) * 100) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[#64748B]">{label}:</span>
      <div className="w-16 h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[#0F172A] font-medium">{current}/{max}</span>
    </div>
  );
}
